import { Chip } from "@mui/material";

export type StatusTone =
  | "success"
  | "error"
  | "warning"
  | "info"
  | "primary"
  | "neutral";

type ToneStyle = { bg: string; color: string; dot: string };

export const statusTones: Record<StatusTone, ToneStyle> = {
  success: { bg: "#E6F4EA", color: "#1E7E34", dot: "#2E7D32" },
  error: { bg: "#FDECEA", color: "#C62828", dot: "#D32F2F" },
  warning: { bg: "#FFF4E5", color: "#B26A00", dot: "#ED6C02" },
  info: { bg: "#E8F1FD", color: "#1565C0", dot: "#1976D2" },
  primary: { bg: "#EDE7F6", color: "#5E35B1", dot: "#673AB7" },
  neutral: { bg: "#EDEFF2", color: "#5F6771", dot: "#9AA0A6" },
};

export type StatusChipOption = { label?: string; tone?: StatusTone };

/** Maps a raw cell value (lower-cased) to a label + tone. */
export type StatusChipMap = Record<string, StatusTone | StatusChipOption>;

/**
 * Named maps so a column only has to say `statusPreset: "meetingStatus"`.
 * Add a new entry here whenever another module needs a status column.
 */
export const statusChipPresets: Record<string, StatusChipMap> = {
  boolean: {
    true: { label: "Active", tone: "success" },
    false: { label: "Inactive", tone: "neutral" },
  },
  meetingStatus: {
    scheduled: { label: "Scheduled", tone: "info" },
    inprogress: { label: "Inprogress", tone: "warning" },
    completed: { label: "Completed", tone: "success" },
    cancelled: { label: "Cancelled", tone: "error" },
  },
};

export type StatusChipProps = {
  value: any;
  /** Key from `statusChipPresets`. Defaults to the true/false map. */
  preset?: string;
  /** Inline map, merged over the preset, for one-off columns. */
  map?: StatusChipMap;
  /** Back-compat shortcuts for the boolean preset. */
  activeLabel?: string;
  inactiveLabel?: string;
  /** Tone used when the value is not in the map. */
  fallbackTone?: StatusTone;
  size?: "small" | "medium";
};

function toOption(entry?: StatusTone | StatusChipOption): StatusChipOption {
  return typeof entry === "string" ? { tone: entry } : entry || {};
}

function titleCase(value: string) {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

export function StatusChip(props: StatusChipProps) {
  const {
    value,
    preset = "boolean",
    map,
    activeLabel,
    inactiveLabel,
    fallbackTone = "neutral",
    size = "small",
  } = props;

  const statusMap: StatusChipMap = { ...(statusChipPresets[preset] || {}), ...(map || {}) };
  const isBooleanMap = preset === "boolean" && !map;

  // The boolean preset keeps the old behaviour: anything falsy reads as inactive.
  const key = isBooleanMap
    ? String(Boolean(value))
    : String(value ?? "").trim().toLowerCase();

  const option = toOption(statusMap[key]);
  const tone = statusTones[option.tone || fallbackTone] || statusTones.neutral;

  let label = option.label || titleCase(String(value ?? "").trim()) || "-";
  if (isBooleanMap) {
    label = (key === "true" ? activeLabel : inactiveLabel) || option.label || label;
  }

  return (
    <Chip
      size={size}
      label={label}
      icon={
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            marginLeft: 10,
            backgroundColor: tone.dot,
          }}
        />
      }
      sx={{
        backgroundColor: tone.bg,
        color: tone.color,
        fontWeight: 500,
        border: "none",
      }}
    />
  );
}

export default StatusChip;
